import React, { useState } from 'react';
import { toast } from '../utils/toast';
import { geminiApi } from '../services/geminiApi';
import { QuickStart } from '../components/QuickStart';
import { VoiceRecorder } from '../components/VoiceRecorder';

export function LifeCoach() {
  const [developmentInput, setDevelopmentInput] = useState('');
  const [approach, setApproach] = useState('strategic');
  const [focusArea, setFocusArea] = useState('leadership');
  const [objective, setObjective] = useState('skill-development');
  const [duration, setDuration] = useState('3months');
  const [generatedGuidance, setGeneratedGuidance] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const durationOptions = [
    { value: '1month', label: '1 Month' },
    { value: '3months', label: '3 Months' },
    { value: '6months', label: '6 Months' },
    { value: '1year', label: '1 Year' }
  ];

  const approachOptions = [
    { value: 'strategic', label: 'Strategic Planning' },
    { value: 'performance', label: 'Performance Optimization' },
    { value: 'leadership', label: 'Leadership Development' },
    { value: 'innovation', label: 'Innovation & Growth' },
    { value: 'operational', label: 'Operational Excellence' }
  ];

  const focusAreaOptions = [
    { value: 'leadership', label: 'Leadership Skills' },
    { value: 'management', label: 'Team Management' },
    { value: 'communication', label: 'Professional Communication' },
    { value: 'strategy', label: 'Strategic Planning' },
    { value: 'productivity', label: 'Productivity & Efficiency' },
    { value: 'project', label: 'Project Management' }
  ];

  const objectiveOptions = [
    { value: 'skill-development', label: 'Skill Development' },
    { value: 'career-growth', label: 'Career Advancement' },
    { value: 'team-building', label: 'Team Building' },
    { value: 'problem-solving', label: 'Problem Resolution' },
    { value: 'change-management', label: 'Change Management' }
  ];

  const quickStartGuides = [
    {
      title: "Development Strategy",
      description: "Define your professional development goals and identify key areas for growth.",
      keywords: ["Goals", "Skills", "Growth"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      )
    },
    {
      title: "AI Guidance",
      description: "Receive data-driven insights and actionable recommendations for professional growth.",
      keywords: ["Analysis", "Insights", "Action Steps"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    {
      title: "Implementation",
      description: "Create an actionable development plan with measurable milestones.",
      keywords: ["Planning", "Execution", "Results"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 7v2m6-2v2" />
        </svg>
      )
    }
  ];

  const handleVoiceTranscriptionComplete = (transcribedText) => {
    setDevelopmentInput(transcribedText);
  };

  const generateGuidance = async () => {
    if (!developmentInput.trim()) {
      toast.error('Please provide details about your professional development goals');
      return;
    }

    setIsGenerating(true);
    try {
      const prompt = `Create a personalized professional development plan for: ${developmentInput}
Development Approach: ${approach}
Focus Area: ${focusArea}
Primary Objective: ${objective}
Duration: ${duration}

Please provide:
- Detailed goals and objectives
- Step-by-step action plan
- Key milestones and timeline
- Success metrics and KPIs
- Resource recommendations
- Progress tracking methods
- Potential challenges and solutions

Format the response as a structured development plan with clear sections and actionable items.`;

      const response = await geminiApi.generateText(prompt);

      if (response.error) {
        throw new Error(response.error);
      } else {
        // Check for both content and message properties
        const generatedText = response.content || response.message;
        if (generatedText) {
          setGeneratedGuidance(generatedText);
          toast.success('Development plan generated successfully!');
        } else {
          throw new Error('No content received from the API');
        }
      }
    } catch (error) {
      console.error('Error generating guidance:', error);
      toast.error(error.message || 'Failed to generate development plan');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Modern SaaS Header */}
      <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 shadow-2xl overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0">
          {/* Modern grid pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          
          {/* Top and bottom subtle lines */}
          <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-gray-400/20 to-transparent"></div>
          <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gray-400/20 to-transparent"></div>
          
          {/* Glowing orbs */}
          <div className="absolute -left-48 -top-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-gray-500/40 via-gray-300/10 to-gray-400/40 blur-xl opacity-30"></div>
          </div>
          <div className="absolute -right-48 -bottom-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-gray-400/40 via-gray-300/10 to-gray-500/40 blur-xl opacity-30"></div>
          </div>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-gray-800/50 border border-gray-700/50 backdrop-blur-sm">
                <div className="w-2 h-2 rounded-full bg-gray-400 animate-pulse"></div>
                <span className="text-sm text-gray-100">AI-Powered Development</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                Professional Development
              </h1>
              <p className="text-lg text-gray-200 max-w-xl">
                Accelerate your career growth with data-driven guidance
              </p>
              <div className="flex items-center gap-4 pt-2">
                <div className="flex items-center space-x-2 text-sm text-gray-200">
                  <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Strategic Planning</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-200">
                  <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Skill Development</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .bg-gradient-conic {
          background-image: conic-gradient(var(--tw-gradient-stops));
        }
      `}</style>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
        <div className="p-6">
          <div className="space-y-8">
            {/* Input Section */}
            <div className="space-y-6">
              {/* Development Input with Voice Recording */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Professional Development Goals
                </label>
                <VoiceRecorder 
                  onTranscriptionComplete={handleVoiceTranscriptionComplete}
                  placeholder="Click to record your development goals..."
                  className="mb-4"
                />
                <textarea
                  value={developmentInput}
                  onChange={(e) => setDevelopmentInput(e.target.value)}
                  rows="4"
                  placeholder="Record or type your professional development objectives..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                />
              </div>

              {/* Development Approach */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Development Approach
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {approachOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setApproach(option.value)}
                      className={`flex items-center justify-center p-3 rounded-lg border ${
                        approach === option.value
                          ? 'bg-gray-50 border-gray-500 text-gray-700'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Focus Area */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Focus Area
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {focusAreaOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setFocusArea(option.value)}
                      className={`flex items-center justify-center p-3 rounded-lg border ${
                        focusArea === option.value
                          ? 'bg-gray-50 border-gray-500 text-gray-700'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Development Objective */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Objective
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {objectiveOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setObjective(option.value)}
                      className={`flex items-center justify-center p-3 rounded-lg border ${
                        objective === option.value
                          ? 'bg-gray-50 border-gray-500 text-gray-700'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={generateGuidance}
                disabled={isGenerating}
                className={`w-full px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                  isGenerating
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gray-600 hover:bg-gray-700'
                }`}
              >
                {isGenerating ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating Plan...
                  </div>
                ) : (
                  'Generate Development Plan'
                )}
              </button>
            </div>

            {/* Generated Guidance Section */}
            {generatedGuidance ? (
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Development Plan</h3>
                  <button
                    onClick={() => copyToClipboard(generatedGuidance)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
                <div className="prose max-w-none">
                  <div className="whitespace-pre-wrap">{generatedGuidance}</div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No development plan yet</h3>
                <p className="mt-1 text-sm text-gray-500">Define your professional development goals to get started.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <QuickStart guides={quickStartGuides} />
    </div>
  );
}

export default LifeCoach; 